import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MailCheck } from 'lucide-react';
import { forgotPasswordSchema, type ForgotPasswordInput } from '@rhpc/shared';
import { AuthBrand } from '@/components/BrandMark';
import { Button, Input } from '@rhpc/ui';

export function ForgotPasswordPage() {
  const [submitted, setSubmitted] = useState(false);

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<ForgotPasswordInput>({
    resolver: zodResolver(forgotPasswordSchema),
    mode: 'onBlur',
  });

  const onSubmit = async (data: ForgotPasswordInput) => {
    await fetch('/api/v1/auth/forgot-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    setSubmitted(true);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg p-8">
      <div className="w-full max-w-[392px]">
        <AuthBrand title="Forgot Password?" subtitle="Claim System" />

        <div className="rounded-lg border border-border bg-surface p-8 shadow-sm">
          {submitted ? (
            <div className="space-y-4 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-info-soft text-info">
                <MailCheck className="h-6 w-6" strokeWidth={2} />
              </div>
              <p className="text-sm font-bold text-text">Reset link sent</p>
              <p className="text-sm text-text-2">
                If this email is registered, a password reset link has been sent. Check your inbox.
              </p>
              <Link to="/login" className="block text-sm font-bold text-primary-text hover:underline">
                ← Back to Sign In
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
              <p className="text-sm text-text-2">
                Enter your email and we&apos;ll send you a reset link.
              </p>
              <Input
                id="email"
                label="Email Address"
                type="email"
                placeholder="you@robusthpc.com"
                required
                error={errors.email?.message}
                {...register('email')}
              />
              <Button type="submit" loading={isSubmitting} className="w-full">
                Send Reset Link
              </Button>
              <Link to="/login" className="block text-center text-sm font-semibold text-text-2 hover:text-primary-text">
                ← Back to Sign In
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
